<template>
    <div>
        <Toast group="appToast" position="top-right" class="toast-custom" />
    </div>
</template>

<script setup>

    // based imports
    import { inject } from 'vue';

    // services imports
    import { useToast } from 'primevue/usetoast';

    // definisco il servizio del toast
    const toastBus = inject('toastBus');
    const toast = useToast();

    // definisco le opzioni base del toast
    const baseOptions = {
        group: 'appToast',
        life: 4000,
        closable: true,
        className: 'modern-toast'
    };

    // Configurazioni per i diversi tipi di toast
    const toastConfigs = {
        success: {
            severity: 'success',
            summary: 'Operazione Completata',
            icon: 'pi pi-check-circle',
            style: {
                background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                boxShadow: '0 8px 16px rgba(34, 197, 94, 0.2)'
            }
        },
        error: {
            severity: 'error',
            summary: 'Errore',
            icon: 'pi pi-times-circle',
            style: {
                background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                boxShadow: '0 8px 16px rgba(239, 68, 68, 0.2)'
            }
        },
        warning: {
            severity: 'warn',
            summary: 'Attenzione',
            icon: 'pi pi-exclamation-triangle',
            style: {
                background: 'linear-gradient(135deg, #f97316, #ea580c)',
                boxShadow: '0 8px 16px rgba(249, 115, 22, 0.2)'
            }
        },
        info: {
            severity: 'info',
            summary: 'Informazione',
            icon: 'pi pi-info-circle',
            style: {
                background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                boxShadow: '0 8px 16px rgba(59, 130, 246, 0.2)'
            }
        }
    };

    // definisco la funzione per mostrare il toast 
    const showToast = (message, type = 'info') => {
        const config = toastConfigs[type] || toastConfigs.info;
        toast.add({
            ...baseOptions,
            ...config,
            summary: message.summary,
            detail: message.detail
        });
    };

    // definisco gli eventi per il toast
    toastBus.on('success', (message) => showToast(message, 'success'));
    toastBus.on('error', (message) => showToast(message, 'error'));
    toastBus.on('warning', (message) => showToast(message, 'warning'));
    toastBus.on('info', (message) => showToast(message, 'info'));

</script>

<style>
.p-toast {
    @apply font-sans;
}

.p-toast .p-toast-message {
    @apply border-none rounded-xl overflow-hidden m-3;
    animation: toastSlideIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
    backdrop-filter: blur(8px);
    position: relative;
}

.p-toast .p-toast-message-content {
    @apply p-4 flex items-center gap-4;
    background: rgba(255, 255, 255, 0.1);
    position: relative;
    overflow: hidden;
}

/* Progress bar */
.p-toast .p-toast-message::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    height: 3px;
    width: 100%;
    animation: progressBar 4s linear forwards;
}

/* Progress bar colors per type */
.p-toast .p-toast-message-success::after {
    background: rgba(134, 239, 172, 0.5);
}

.p-toast .p-toast-message-error::after {
    background: rgba(252, 165, 165, 0.5);
}

.p-toast .p-toast-message-warn::after {
    background: rgba(253, 186, 116, 0.5);
}

.p-toast .p-toast-message-info::after {
    background: rgba(147, 197, 253, 0.5);
}

/* Icon animations */
.p-toast .p-toast-message-icon {
    @apply text-3xl relative;
    animation: iconEntrance 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
}

.p-toast .p-toast-message-icon::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 50%;
    transform: translateX(-50%);
    width: 20px;
    height: 2px;
    border-radius: 4px;
    opacity: 0;
    animation: iconUnderline 0.3s ease forwards 0.5s;
}

/* Icon underline colors */
.p-toast-message-success .p-toast-message-icon::after {
    background: rgba(134, 239, 172, 0.8);
}

.p-toast-message-error .p-toast-message-icon::after {
    background: rgba(252, 165, 165, 0.8);
}

.p-toast-message-warn .p-toast-message-icon::after {
    background: rgba(253, 186, 116, 0.8);
}

.p-toast-message-info .p-toast-message-icon::after {
    background: rgba(147, 197, 253, 0.8);
}

/* Light mode styles */
.p-toast .p-toast-message-success {
    background: linear-gradient(135deg, rgba(34, 197, 94, 0.1), rgba(22, 163, 74, 0.2)) !important;
    backdrop-filter: blur(8px);
    border: 1px solid rgba(34, 197, 94, 0.2);
}

.p-toast .p-toast-message-error {
    background: linear-gradient(135deg, rgba(239, 68, 68, 0.1), rgba(220, 38, 38, 0.2)) !important;
    backdrop-filter: blur(8px);
    border: 1px solid rgba(239, 68, 68, 0.2);
}

.p-toast .p-toast-message-warn {
    background: linear-gradient(135deg, rgba(249, 115, 22, 0.1), rgba(234, 88, 12, 0.2)) !important;
    backdrop-filter: blur(8px);
    border: 1px solid rgba(249, 115, 22, 0.2);
}

.p-toast .p-toast-message-info {
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(37, 99, 235, 0.2)) !important;
    backdrop-filter: blur(8px);
    border: 1px solid rgba(59, 130, 246, 0.2);
}

/* Dark mode styles */
@media (prefers-color-scheme: dark) {
    .p-toast .p-toast-message-success {
        background: linear-gradient(135deg, rgba(34, 197, 94, 0.2), rgba(22, 163, 74, 0.3)) !important;
        backdrop-filter: blur(8px);
    }

    .p-toast .p-toast-message-error {
        background: linear-gradient(135deg, rgba(239, 68, 68, 0.2), rgba(220, 38, 38, 0.3)) !important;
        backdrop-filter: blur(8px);
    }

    .p-toast .p-toast-message-warn {
        background: linear-gradient(135deg, rgba(249, 115, 22, 0.2), rgba(234, 88, 12, 0.3)) !important;
        backdrop-filter: blur(8px);
    }

    .p-toast .p-toast-message-info {
        background: linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(37, 99, 235, 0.3)) !important;
        backdrop-filter: blur(8px);
    }
}

/* Animations */
@keyframes progressBar {
    0% {
        width: 100%;
    }
    100% {
        width: 0%;
    }
}

@keyframes iconEntrance {
    0% {
        transform: scale(0) rotate(-180deg);
        opacity: 0;
    }
    60% {
        transform: scale(1.2) rotate(10deg);
    }
    80% {
        transform: scale(0.9) rotate(-5deg);
    }
    100% {
        transform: scale(1) rotate(0);
        opacity: 1;
    }
}

@keyframes iconUnderline {
    0% {
        width: 0;
        opacity: 0;
    }
    100% {
        width: 20px;
        opacity: 1;
    }
}

.p-toast .p-toast-message:hover .p-toast-message-icon {
    animation: iconPulse 1s ease-in-out infinite;
}

@keyframes iconPulse {
    0%, 100% {
        transform: scale(1);
    }
    50% {
        transform: scale(1.1);
    }
}

.p-toast .p-toast-message-exit-active {
    animation: toastSlideOut 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
}

.p-toast .p-toast-message-exit-active::after {
    animation: none;
    width: 0;
}

.p-toast .p-toast-summary {
    @apply font-bold text-base mb-1;
    animation: textSlide 0.3s ease forwards;
    animation-delay: 0.1s;
    opacity: 0;
}

.p-toast .p-toast-detail {
    @apply text-sm;
    animation: textSlide 0.3s ease forwards;
    animation-delay: 0.2s;
    opacity: 0;
}

.p-toast .p-toast-icon-close {
    @apply text-white/70 hover:text-white transition-all duration-300 !w-8 !h-8 !rounded-full flex items-center justify-center;
    background: rgba(255, 255, 255, 0.1);
    margin: 0.5rem;
    animation: fadeIn 0.3s ease forwards;
    animation-delay: 0.6s;
    opacity: 0;
}

.p-toast .p-toast-icon-close:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: rotate(180deg) scale(1.2);
}

.p-toast .p-toast-message:hover {
    transform: translateY(-3px);
    transition: all 0.3s ease;
}

/* Nuove animazioni */
@keyframes toastSlideIn {
    0% {
        transform: translateX(100%);
        opacity: 0;
    }
    50% {
        transform: translateX(-10%);
    }
    65% {
        transform: translateX(5%);
    }
    80% {
        transform: translateX(-2%);
    }
    100% {
        transform: translateX(0);
        opacity: 1;
    }
}

@keyframes toastSlideOut {
    0% {
        transform: translateX(0);
        opacity: 1;
    }
    100% {
        transform: translateX(100%);
        opacity: 0;
    }
}

@keyframes textSlide {
    0% {
        transform: translateY(100%);
        opacity: 0;
    }
    100% {
        transform: translateY(0);
        opacity: 1;
    }
}

/* Stili specifici per tipo con bordi luminosi e ombre colorate */
.p-toast .p-toast-message.p-toast-message-success {
    border-left: 4px solid rgba(134, 239, 172, 0.5);
}

.p-toast .p-toast-message.p-toast-message-error {
    border-left: 4px solid rgba(252, 165, 165, 0.5);
}

.p-toast .p-toast-message.p-toast-message-warn {
    border-left: 4px solid rgba(253, 186, 116, 0.5);
}

.p-toast .p-toast-message.p-toast-message-info {
    border-left: 4px solid rgba(147, 197, 253, 0.5);
}

/* Animazioni specifiche per tipo */
.p-toast .p-toast-message.p-toast-message-success .p-toast-message-icon {
    animation: iconSuccess 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
}

.p-toast .p-toast-message.p-toast-message-error .p-toast-message-icon {
    animation: iconError 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
}

.p-toast .p-toast-message.p-toast-message-warn .p-toast-message-icon {
    animation: iconWarn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
}

.p-toast .p-toast-message.p-toast-message-info .p-toast-message-icon {
    animation: iconInfo 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
}

@keyframes fadeIn {
    0% {
        opacity: 0;
    }
    100% {
        opacity: 1;
    }
}
</style>